import React, { useMemo } from 'react';
import {
  Autocomplete,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { useFilters } from '@/contexts/FilterContext';
import { useProducts } from '@/contexts/ProductsContext';
import { useAdminPermissions } from '@/hooks/useAdminPermissions';
import type { ProductFilterMode } from '@/config/productFilters';
import { ProductOptionLabel, productDisplayName } from '@/components/ProductOptionLabel';

const EMPTY_VALUE = '';

type ProductFilterProps = {
  mode: ProductFilterMode;
};

/**
 * Filtro único de produto no topo do admin.
 * - single-required: vazio = nenhum produto (conteúdo bloqueado até selecionar)
 * - single-all: vazio = todos os produtos
 * - multi-all: nenhum chip = todos os produtos
 */
export const ProductFilter: React.FC<ProductFilterProps> = ({ mode }) => {
  const { productId, setProductId, selectedProductIds, setSelectedProductIds } = useFilters();
  const { admin } = useAdminPermissions();
  const { products, loading } = useProducts();

  const options = useMemo(() => {
    const scopes = admin?.productScopes?.length
      ? admin.productScopes
      : admin?.role === 'super_admin'
        ? ['*']
        : ['vover-app'];
    const canAccess = (id: string) => scopes.includes('*') || scopes.includes(id);
    return products.filter(p => canAccess(p.productId));
  }, [products, admin?.productScopes, admin?.role]);

  if (loading && options.length === 0) {
    return <CircularProgress size={22} sx={{ ml: 1 }} />;
  }

  if (mode === 'multi-all') {
    const selected = options.filter(o => selectedProductIds.includes(o.productId));
    return (
      <Autocomplete
        multiple
        size="small"
        options={options}
        value={selected}
        onChange={(_, value) => setSelectedProductIds(value.map(v => v.productId))}
        getOptionLabel={o => productDisplayName(o.displayName, o.productId)}
        isOptionEqualToValue={(a, b) => a.productId === b.productId}
        renderOption={(props, option) => (
          <li {...props} key={option.productId}>
            <ProductOptionLabel displayName={option.displayName} productId={option.productId} />
          </li>
        )}
        renderInput={params => (
          <TextField
            {...(params as React.ComponentProps<typeof TextField>)}
            size="small"
            label="Produtos"
            placeholder={selected.length ? '' : 'Selecione um produto (todos)'}
            sx={{ minWidth: 300 }}
          />
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => {
            const { key, ...chipProps } = getTagProps({ index });
            return (
              <Tooltip key={key} title={option.productId} arrow placement="top" enterDelay={300}>
                <Chip
                  {...chipProps}
                  label={productDisplayName(option.displayName, option.productId)}
                  size="small"
                />
              </Tooltip>
            );
          })
        }
        sx={{ ml: 1 }}
      />
    );
  }

  const selectValue =
    productId && options.some(p => p.productId === productId) ? productId : EMPTY_VALUE;

  const emptyLabel =
    mode === 'single-required' ? 'Selecione um produto' : 'Selecione um produto (todos)';

  return (
    <FormControl size="small" sx={{ minWidth: 280, ml: 1 }}>
      <InputLabel id="product-filter-label">Produto</InputLabel>
      <Select
        labelId="product-filter-label"
        label="Produto"
        value={selectValue}
        onChange={e => setProductId(String(e.target.value))}
        renderValue={selected => {
          if (!selected) return emptyLabel;
          const p = options.find(o => o.productId === selected);
          return <ProductOptionLabel displayName={p?.displayName} productId={String(selected)} />;
        }}
      >
        <MenuItem value={EMPTY_VALUE}>
          <Typography variant="body2" color="text.secondary">
            {emptyLabel}
          </Typography>
        </MenuItem>
        {options.map(p => (
          <MenuItem key={p.productId} value={p.productId}>
            <ProductOptionLabel displayName={p.displayName} productId={p.productId} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
